/**
	* This component is used in the navbar to hold filters
	* in a dropdown-style button. Main difference from
	* DropdownButton component is this one contains 'reset'
	* and 'done' buttons along bottom of dropdown menu.
**/
import React, { MutableRefObject, ReactElement, ReactFragment } from 'react';
import * as bootstrap from 'bootstrap';
import classNames from 'classnames';
import { Button } from '@capcenter/shared';

import './FilterDropdown.scss';

interface BottomButtonProps {
	onResetClick: (value: boolean) => void,
	onDoneClick?: () => void,
	disableSubmitBtn?: boolean,
	resetLabel?: string,
}

interface FilterDropdownProps {
	id: string,
	active: boolean, // { true } makes dropdown button appear active
	label?: string | ReactFragment,
	labelClass?: string,
	autoclose?: boolean | string,
	onDoneClick?: () => void,
	onResetClick: () => void,
	options: ReactElement,
	outline?: boolean,
	className?: string,
	wrapperClass?: string,
	disableSubmitBtn?: boolean,
	setDisableSubmitBtn?: (value: boolean) => void,
	icon?: string ,
	customIcon?: HTMLElement | ReactElement | false,
	invalid?: boolean,
	setInvalid?: (value: boolean) => void,
	resetLabel?: string,
	disableBottomButtons?: boolean
	disableActive?: boolean
}

const DropdownBottomButtons = ({ onResetClick, onDoneClick, disableSubmitBtn, resetLabel }: BottomButtonProps) => {
	return (
		<div className="w-100 d-flex justify-content-between mt-2">
			<Button
				resetText
				variant="flat"
				color="secondary"
				label={resetLabel ? resetLabel : 'Reset'}
				size="sm"
				icon="far fa-undo"
				onClick={onResetClick}
			/>
			<Button
				disabled={disableSubmitBtn}
				resetText
				variant="filled"
				color="secondary"
				label="Done"
				icon="fas fa-check"
				size="md"
				onClick={onDoneClick}
			/>
		</div>
	);
};

const FilterDropdown = ({ id, active, label, labelClass, autoclose, onDoneClick, onResetClick, options, className, wrapperClass, disableSubmitBtn, icon, customIcon, invalid, resetLabel, disableBottomButtons, disableActive, outline = true }: FilterDropdownProps) => {

	const [dropdownElement, setDropdownElement] = React.useState<bootstrap.Dropdown>();
	const [show, setShow] = React.useState(false);

	const ref = React.useRef() as MutableRefObject<HTMLDivElement>;

	React.useEffect(() => {
		const dropdown = document.getElementById(id);
		const dropdownTriggerElement = bootstrap.Dropdown.getOrCreateInstance(dropdown as HTMLElement);
		setDropdownElement(dropdownTriggerElement);

		dropdown?.addEventListener('show.bs.dropdown', function () { setShow(true); });
		dropdown?.addEventListener('hide.bs.dropdown', function () { setShow(false); });
	}, [id]);


	function hideDropdownOnOffClick(event: Event) {
		const toastContainer = document.getElementById('filterWarningToastContainer');

		// If click happened on dropdown, trigger, or a toast notif, do nothing
		if ((ref && ref.current instanceof Node && ref.current.contains(event.target as ChildNode)) || (toastContainer && toastContainer.contains(event.target as ChildNode))) {
			return;
		}

		dropdownElement && dropdownElement.hide();  // Close dropdown
		document.removeEventListener('mousedown', hideDropdownOnOffClick);  // Remove click event listener
	}

	function handleReset() {
		onResetClick();
		return dropdownElement?.hide();
	}

	function handleDropdownButtonClick() {
		if (!show) {
			dropdownElement?.show();
			return document.addEventListener('mousedown', (e: Event) => hideDropdownOnOffClick(e));  // Listen for next click
		}
		return dropdownElement?.hide();
	}

	return (
		<div ref={ref} className={classNames(wrapperClass && wrapperClass, 'dropdown position-relative')}>
			<button
				id={id}
				className={classNames(active ? 'cc-btn--secondary homesearch-filters__cc-btn--active' : disableActive === true ? '' : 'cc-btn--gray', className && className, outline && 'cc-btn--outline', 'cc-btn cc-btn--reset-text justify-content-center')}
				type="button"
				data-bs-toggle="dropdown"
				data-bs-reference="parent"
				data-bs-auto-close={autoclose ? autoclose : false}
				aria-expanded="false"
				onClick={handleDropdownButtonClick}
			>
				<div className={classNames(labelClass && labelClass)}>{label}</div>

				{customIcon && customIcon}

				{icon && <span className={classNames(icon ? icon : show ? 'filter-dropdown-icon--flip-vertically far fa-times' : 'fas fa-caret-down', 'filter-dropdown-icon text-secondary ms-2')} />}
			</button>

			<ul id={`${id}-options`} className="dropdown-menu dropdown-menu-end p-3" aria-labelledby={id} role="menubar">

				{options}

				{disableBottomButtons !== true &&
					<div className={classNames(invalid ? 'pt-3' : '', 'd-flex w-100 justify-content-between pb-4 mb-4')}>
						<DropdownBottomButtons
							onResetClick={handleReset}
							onDoneClick={() => {
								dropdownElement?.hide();
								onDoneClick && onDoneClick();
							}}
							disableSubmitBtn={disableSubmitBtn}
							resetLabel={resetLabel}
						/>
					</div>
				}
			</ul>
		</div>
	);
};

export default FilterDropdown;
